import React, { useState, useMemo } from "react";
import {
  FiCamera,
  FiEye,
  FiKey,
  FiUser,
  FiMail,
  FiPhone,
  FiSave,
} from "react-icons/fi";
import SelectDropdown from "../shared/SelectDropdown";
import Input from "../shared/Input";
import "./TabProfile.css";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

export const formatPhone = (phone = "") => {
  let digits = phone.toString().replace(/\D/g, "");

  // +90 ile gelenler
  if (digits.startsWith("90") && digits.length <= 12) {
    return `+90 (${digits.slice(2, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`.trim();
  }

  // 0 ile başlayanlar
  if (digits.startsWith("0")) {
    return `0 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`.trim();
  }

  // 5 ile başlayanlar
  if (digits.startsWith("5")) {
    return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`.trim();
  }

  return digits;
};

const TabProfile = ({
  adminData,
  handleInputChange,
  handleAvatarSelect,
  avatarList,
  handleSubmit,
  rolesOptions,
  selectedRole,
  setSelectedRole,
  languagesOptions,
  selectedLanguage,
  setSelectedLanguage,
  warehouseOptions,
  selectedWarehouse,
  setSelectedWarehouse,
  setAdminData,
}) => {
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const avatarUrl = adminData.adminImage
    ? `${process.env.REACT_APP_API_BASE_URL}${adminData.adminImage}`
    : `${process.env.REACT_APP_API_BASE_URL}/uploads/avatars/default.svg`;

  const toFullUrl = (path) =>
    path.startsWith("http")
      ? path
      : `${process.env.REACT_APP_API_BASE_URL}${path}`;

  // sadece rakamları al
  const onlyDigits = (val = "") => val.replace(/\D/g, "");

  // ekranda formatlı göster
  const formatPhoneInput = (value) => {
    const digits = onlyDigits(value);
    return formatPhone(digits);
  };

  return (
    <div className="tab-pane fade show active" id="profileTab" role="tabpanel">
      <div className="card-body personal-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">{getText("ADMIN_INFO")}</span>
          </h5>
        </div>

        {/* Avatar Seçme */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">
              {getText("TABLE_PROFILE_IMAGE_LABEL")}
            </label>
          </div>
          <div className="col-lg-8">
            <div className="d-flex align-items-center gap-3">
              <img
                src={avatarUrl}
                alt="Selected Avatar"
                className="rounded-circle avatar-preview"
                onClick={() => setShowAvatarModal(true)}
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowAvatarModal(true)}
              >
                <FiCamera className="me-2" /> {getText("PROFILE_SELECT")}
              </button>
            </div>
          </div>
        </div>

        <Input
          icon={<FiUser />}
          label={getText("TABLE_FIRST_NAME_LABEL")}
          name="first_name"
          placeholder={getText("TABLE_FIRST_NAME_LABEL")}
          value={adminData.first_name}
          onChange={handleInputChange}
        />
        <Input
          icon={<FiUser />}
          label={getText("TABLE_LAST_NAME_LABEL")}
          name="last_name"
          placeholder={getText("TABLE_LAST_NAME_LABEL")}
          value={adminData.last_name}
          onChange={handleInputChange}
        />
        <Input
          icon={<FiMail />}
          label={getText("TABLE_EMAIL_LABEL")}
          name="email"
          type="email"
          placeholder="example@example.com"
          value={adminData.email}
          onChange={handleInputChange}
        />
        <input
          type="text"
          name="email"
          autoComplete="username"
          style={{ display: "none" }}
        />
        <input
          type="password"
          name="password"
          autoComplete="new-password"
          style={{ display: "none" }}
        />

        <Input
          icon={<FiPhone />}
          label={getText("TABLE_PHONE_LABEL")}
          name="phone"
          type="tel"
          placeholder="0 (5xx) xxx xx xx"
          inputMode="numeric"
          autoComplete="off"
          value={adminData.phone || ""}
          onChange={(e) => {
            const formatted = formatPhoneInput(e.target.value);

            setAdminData((prev) => ({
              ...prev,
              phone: formatted, // 👈 ekranda formatlı
            }));
          }}
        />
      </div>

      <hr className="my-0" />

      {/* Ek Bilgiler */}
      <div className="card-body additional-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">{getText("GENERAL_INFO")}</span>
          </h5>
        </div>

        {/* Rol */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">
              {getText("LABEL_ROLE")}
            </label>
          </div>
          <div className="col-lg-8">
            <SelectDropdown
              inputProps={{ autoComplete: "off", name: "role_select_input" }}
              // 2️⃣ stringRolesOptions’a geçiyoruz
              options={rolesOptions}
              selectedOption={selectedRole}
              onSelectOption={(opt) => setSelectedRole(opt)}
              defaultSelect={getText("ROLE_PLACEHOLDER")}
            />
          </div>
        </div>

        {/* Yönetim Paneli Dili */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">
              {getText("TABLE_ADMIN_LANGUAGE_LABEL")}
            </label>
          </div>
          <div className="col-lg-8">
            <SelectDropdown
              options={languagesOptions}
              selectedOption={selectedLanguage}
              onSelectOption={(val) => {
                // 1) debug
                console.log("Seçilen:", val, typeof val);

                // 2) string karşılaştırmalı eşle
                const opt = languagesOptions.find(
                  (l) => String(l.value) === String(val.value ?? val), // val obje ise val.value, değilse val
                );

                if (opt) setSelectedLanguage(opt);
              }}
              defaultSelect={getText("LANG_PLACEHOLDER")}
            />
          </div>
        </div>
      </div>

      <hr className="my-0" />

      {/* Şifre Bilgileri */}
      <div className="card-body pass-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">
              {getText("PROFILE_PASSWORD_INFO")}
            </span>
          </h5>
        </div>

        {/* Password Input */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">{getText("PROFILE_PASSWORD")}</label>
          </div>
          <div className="col-lg-8">
            <div className="input-group">
              <div className="input-group-text">
                <FiKey />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                name="password"
                placeholder={getText("PROFILE_PASSWORD_PLACEHOLDER")}
                value={adminData.password || ""}
                onChange={handleInputChange}
              />
              <div
                className="input-group-text border-start bg-gray-2 c-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FiEye />
              </div>
            </div>
          </div>
        </div>

        {/* Password Confirm Input */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">
              {getText("PROFILE_CONFIRM_PASSWORD")}
            </label>
          </div>
          <div className="col-lg-8">
            <div className="input-group">
              <div className="input-group-text">
                <FiKey />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control"
                name="confirmPassword"
                placeholder={getText("PROFILE_CONFIRM_PASSWORD_PLACEHOLDER")}
                value={adminData.confirmPassword || ""}
                onChange={handleInputChange}
              />
              <div
                className="input-group-text border-start bg-gray-2 c-pointer"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <FiEye />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="d-flex justify-content-center mt-4">
          <button
            className="btn btn-primary px-3 py-2.5 fw-bold"
            onClick={handleSubmit}
          >
            <FiSave className="me-2" /> {getText("SAVE_BTN")}
          </button>
        </div>
      </div>

      {/* Avatar Seçim Modalı */}
      {showAvatarModal && (
        <div
          className="avatar-modal-overlay"
          onClick={() => setShowAvatarModal(false)}
        >
          <div
            className="avatar-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-3 fw-bold">Avatar Seç</h4>
            <div className="avatar-selection-container">
              {avatarList.map((avatar, index) => (
                <img
                  key={index}
                  src={toFullUrl(avatar)} // 🔹 Tam URL ekleyerek göster
                  alt="Avatar"
                  className={`avatar-img ${
                    adminData.adminImage === avatar ? "selected" : ""
                  }`}
                  onClick={() => {
                    handleAvatarSelect(
                      avatar.replace(
                        `${process.env.REACT_APP_API_BASE_URL}`,
                        "",
                      ),
                    ); // 🔹 Veritabanına sadece dosya yolunu kaydet
                    setShowAvatarModal(false);
                  }}
                />
              ))}
            </div>
            <button
              className="close-button mt-3"
              onClick={() => setShowAvatarModal(false)}
            >
              {getText("CLOSE_BTN")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabProfile;
